import { useCallback } from "react"
import { toast } from "react-toastify"

import {
  marketingClient,
  paymentClient,
  useQueryClient,
} from "@trueskin-web/apis"
import { trackingService } from "@trueskin-web/services"
import { useTranslation } from "@trueskin-web/translations"

const withServerAction =
  (Component) =>
  ({
    className,
    order,
    primaryPaymentMethod,
    hidePaymentMethods,
    onTokenization,
  }) => {
    const { t } = useTranslation(null, {
      keyPrefix: "Payment.Methods",
    })
    const queryClient = useQueryClient()

    const tokenizeRequest = useCallback(
      async (data) =>
        paymentClient.tokenizePaymentMethod(data).then((result) => {
          if (result.data?.paymentTransactionResult?.status === "failed") {
            const paymentFailedError = new Error("PAYMENT_FAILED")
            paymentFailedError.data = {
              refusalReasonCode:
                result.data.paymentTransactionResult.refusalReasonCode,
            }
            throw paymentFailedError
          }

          return result
        }),

      []
    )

    const request = useCallback(
      async (data, activePaymentMethod) => {
        const isCardPayment =
          !["braintree", "klarna"].includes(data.provider) &&
          data.paymentMethod?.type === "scheme"

        const isTokenizationRequest =
          !data.amount &&
          !data.paymentMethod?.amount &&
          data.provider !== "confirm_charge"

        const marketingEventData = {
          paymentMethod: activePaymentMethod,
          value: order.amount,
          currency: order.currency,
          presetId: order.presetId,
          voucherCode: order.voucherCode,
          userId: order.customerId,
        }

        if (isTokenizationRequest) {
          return tokenizeRequest(data).then(async (result) => {
            await onTokenization?.()
            toast(t("tokenization.description"))
            return result
          })
        }

        if (!isCardPayment) {
          return paymentClient.purchaseOrder(
            trackingService.getTrackingTouchpoints(),
            marketingEventData,
            data
          )
        }

        return tokenizeRequest(data).then((result) =>
          paymentClient.purchaseOrder(
            trackingService.getTrackingTouchpoints(),
            marketingEventData,
            {
              orderId: data.orderId,
              provider: "stored_payment_method",
              paymentMethodId: result?.data?.paymentMethod.id,
              gateway: result?.data?.paymentMethod.gateway,
              v2CustomerId: data.v2CustomerId,
            }
          )
        )
      },
      [t, onTokenization, tokenizeRequest, order]
    )

    const purchase = useCallback(
      async (data, activePaymentMethod) =>
        request(data, activePaymentMethod).then(async (result) => {
          await queryClient.refetchQueries("currentUser")

          return result
        }),
      [request, queryClient]
    )

    const onError = useCallback(({ paymentMethodType, order }) => {
      return marketingClient.sendEvent({
        name: marketingClient.EVENT_TYPES_ENUM.ADD_NATIVE_PAYMENT_METHOD_ERROR,
        data: {
          paymentMethod: paymentMethodType,
          value: order.amount,
          currency: order.currency,
          presetId: order.presetId,
          voucherCode: order.voucherCode,
          userId: order.customerId,
        },
      })
    }, [])

    return (
      <Component
        className={className}
        order={order}
        primaryPaymentMethod={primaryPaymentMethod}
        onTokenization={onTokenization}
        hidePaymentMethods={hidePaymentMethods}
        purchase={purchase}
        onError={onError}
      />
    )
  }

export default withServerAction
